/* eslint-disable camelcase */
import {
  Entity, PrimaryGeneratedColumn, Column, CreateDateColumn, UpdateDateColumn,
  OneToMany, ManyToOne, JoinColumn, AfterLoad, OneToOne,
} from 'typeorm';
import {
  IsEmail, IsDate, IsOptional, IsInt, IsString,
} from 'class-validator';
import _ from 'lodash';
import {
  TipoTelefone,
  EscolaridadePaciente,
  EstadoCivilPaciente,
  EstadosBrasil,
  Acompanhamento,
} from '../types';
import { Gestacao } from './Gestacao';
import { Historico } from './Historico';
import { PlanoDeSaude } from './PlanoDeSaude';
import { Consulta } from './Consulta';
import { Vacinacao } from './Vacinacao';
import { ResultadoDeExame } from './ResultadoDeExame';
import { Usuario } from './Usuario';
import { InformacaoParto } from './InformacaoParto';
import { Internacao } from './Internacao';
import { Documento } from './Documento';
import { NotaProntuario } from './NotaProntuario';

@Entity({ name: 'pacientes' })
export class Paciente {
  @AfterLoad()
  carregaGestacao?() {
    this.gestacao_atual = _.find(this.gestacoes, (gest) => gest.atual === true) || null;
  }

  @PrimaryGeneratedColumn()
  @IsOptional()
  @IsInt()
  id?: number;

  @Column({ unique: true })
  @IsString()
  cpf: string;

  @Column()
  @IsString()
  rg: string;

  @Column()
  @IsString()
  nome: string;

  @Column({ unique: true })
  @IsEmail()
  email: string;

  @Column()
  @IsOptional()
  foto_aws_key?: string | null;

  @Column()
  @IsDate()
  data_nascimento: Date;

  @Column()
  @IsOptional()
  altura_cm?: number | null;

  @Column()
  @IsString()
  profissao: string;

  @Column({
    type: 'enum',
    enum: EscolaridadePaciente,
  })
  escolaridade: EscolaridadePaciente;

  @Column({
    type: 'enum',
    enum: EstadoCivilPaciente,
    default: EstadoCivilPaciente.S,
  })
  estado_civil: EstadoCivilPaciente;

  @Column()
  @IsOptional()
  estado_civil_outro?: string | null;

  @Column()
  @IsString()
  telefone1: string;

  @Column({
    type: 'enum',
    enum: TipoTelefone,
  })
  tipo_telefone1: string;

  @Column()
  @IsOptional()
  telefone2?: string | null;

  @Column({
    type: 'enum',
    enum: TipoTelefone,
  })
  @IsOptional()
  tipo_telefone2?: string | null;

  @Column()
  @IsOptional()
  condicoes_especiais?: string | null;

  @Column()
  @IsOptional()
  exames?: string | null;

  @Column()
  @IsOptional()
  dados_financeiros?: string | null;

  @Column({
    default: "''",
  })
  @IsOptional()
  observacoes?: string;

  @Column({
    default: 0,
  })
  @IsOptional()
  status_observacoes?: number;

  @Column()
  @IsOptional()
  como_conheceu?: string | null;

  // PARCEIRO(A)
  @Column()
  @IsOptional()
  parceiro_nome?: string | null;

  @Column()
  @IsOptional()
  parceiro_telefone?: string | null;

  @Column({
    type: 'enum',
    enum: TipoTelefone,
  })
  @IsOptional()
  parceiro_tipo_telefone?: string | null;

  @Column()
  @IsOptional()
  parceiro_profissao?: string | null;

  @Column({
    type: 'enum',
    enum: EscolaridadePaciente,
  })
  @IsOptional()
  parceiro_escolaridade?: EscolaridadePaciente;

  // ENDEREÇO DEVE SER REFATORADO
  @Column()
  @IsString()
  @IsOptional()
  cep?: string;

  @Column()
  @IsString()
  @IsOptional()
  logradouro?: string;

  @Column()
  @IsString()
  @IsOptional()
  numero?: string;

  @Column()
  @IsOptional()
  complemento?: string | null;

  @Column()
  @IsOptional()
  bairro?: string | null;

  @Column()
  @IsString()
  @IsOptional()
  cidade?: string;

  @Column({
    type: 'enum',
    enum: EstadosBrasil,
  })
  @IsOptional()
  estado?: EstadosBrasil;

  @Column()
  @IsString()
  @IsOptional()
  pais?: string;

  @Column({
    default: 0,
  })
  @IsOptional()
  status_cadastro?: number;

  @Column({
    type: 'enum',
    enum: Acompanhamento,
  })
  @IsOptional()
  acompanhamento?: string | null;

  @Column()
  @IsOptional()
  responsavel_id?: number | null;

  @Column()
  @IsOptional()
  cancelled?: Date | null;

  @Column()
  @IsOptional()
  cancelled_by?: number | null;

  @Column()
  @IsOptional()
  justificativa_cancelamento?: string | null;

  @Column()
  @IsOptional()
  paridade?: string | null;

  @CreateDateColumn()
  @IsOptional()
  created?: Date;

  @Column()
  @IsOptional()
  created_by?: number | null;

  @UpdateDateColumn()
  @IsOptional()
  updated?: Date;

  @Column()
  @IsOptional()
  updated_by?: number | null;

  // RELATIONS
  @OneToMany(() => Historico, (historicos) => historicos.paciente, {
    eager: true,
  })
  historicos?: Historico[];

  @OneToMany(() => Gestacao, (gestacoes) => gestacoes.paciente, {
    eager: true,
    cascade: ['insert'],
  })
  gestacoes?: Gestacao[];

  gestacao_atual?: Gestacao;

  @OneToMany(() => Internacao, (internacoes) => internacoes.paciente, {
    cascade: ['insert'],
  })
  internacoes?: Internacao[];

  @OneToMany(() => PlanoDeSaude, (planos_de_saude) => planos_de_saude.paciente, {
    eager: true,
    cascade: ['insert'],
  })
  planos_de_saude?: PlanoDeSaude[];

  @OneToMany(() => Documento, (documento) => documento.paciente)
  documentos?: Documento[];

  @OneToMany(() => Consulta, (consulta) => consulta.paciente, {
    cascade: ['update'],
  })
  consultas?: Consulta[];

  @OneToMany(() => NotaProntuario, (nota_prontuario) => nota_prontuario.paciente)
  notas_prontuarios?: NotaProntuario[];

  @OneToMany(() => Vacinacao, (vacinacao) => vacinacao.paciente)
  vacinacoes?: Vacinacao[];

  @OneToMany(() => ResultadoDeExame, (resultados_de_exames) => resultados_de_exames.paciente)
  resultados_de_exames?: ResultadoDeExame[];

  @OneToMany(() => InformacaoParto, (informacao_parto) => informacao_parto.paciente)
  informacoes_parto?: InformacaoParto[];

  @ManyToOne(() => Usuario, { eager: true })
  @JoinColumn({ name: 'responsavel_id' })
  responsavel?: Usuario;

  @ManyToOne(() => Usuario, { eager: true })
  @JoinColumn({ name: 'cancelled_by' })
  usuario_cancelled_by?: Usuario;

  @OneToOne(() => Usuario, (usuario) => usuario.paciente)
  usuario?: Usuario;
}
